import React from 'react';
import { emptyState } from '@techchange/pm-specs';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { FormGroup, Label } from 'reactstrap';

import { sortByWeight } from '@techchange/dnd';
import { TextEditor } from '@techchange/components';
import { messages } from '../../../i18n';

import CertificatePreview from './CertificatePreview';
import TemplateSignatures from './TemplateSignatures';

import styles from './TemplateEditor.module.scss';

const propTypes = {

	/**
	 * The index of the template being edited.
	 */
	index: PropTypes.number.isRequired,

	/**
	 * Flag indicating if styling for wider screens should be applied.
	 */
	widescreen: PropTypes.bool,

	/**
	 * The certificate form data.
	 */
	formData: PropTypes.shape({

		/**
		 * The list of associated templates for the certificate.
		 */
		certificate_templates: PropTypes.arrayOf(PropTypes.shape({

			/**
			 * The UUID for the certificate template.
			 */
			id: PropTypes.string.isRequired,

			/**
			 * The certificate template content, represented as a ProseMirror object.
			 */
			content: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),

			/**
			 * The list of certificate people associated with the template.
			 */
			certificate_people: PropTypes.arrayOf(PropTypes.shape({

				/**
				 * The certificate person's ID.
				 */
				id: PropTypes.string.isRequired,

				/**
				 * The certificate person's weight.
				 */
				weight: PropTypes.number.isRequired,

				/**
				 * The ID of the associated certificate template.
				 */
				template_id: PropTypes.string.isRequired,

				/**
				 * The ID of the associated person.
				 */
				person_id: PropTypes.string.isRequired,

			})),

		})).isRequired,

	}).isRequired,

	/**
	 * The associated certificate layout.
	 */
	layout: PropTypes.shape({

		/**
		 * The HTML template string for the certificate body.
		 */
		html_body: PropTypes.string.isRequired,

	}).isRequired,

	/**
	 * The list of all available people for this organization.
	 */
	people: PropTypes.arrayOf(PropTypes.shape({

		/**
		 * The person's ID.
		 */
		id: PropTypes.string.isRequired,

		/**
		 * The person's name.
		 */
		name: PropTypes.string.isRequired,

		/**
		 * The person's affiliation (i.e. occupation/role).
		 */
		affiliation: PropTypes.string.isRequired,

		/**
		 * The person's signature.
		 */
		signature: PropTypes.string,

	})).isRequired,

	/**
	 * The organization object.
	 */
	organization: PropTypes.shape({

		id: PropTypes.string.isRequired,

		emails: PropTypes.shape({

			primary_color: PropTypes.string,

		}),

	}),

	/**
	 * The filestack credentials object.
	 */
	filestack: PropTypes.shape({

		apiKey: PropTypes.string.isRequired,

		security: PropTypes.shape({

			policy: PropTypes.string.isRequired,

			signature: PropTypes.string.isRequired,

		}).isRequired,

	}).isRequired,

	/**
	 * Callback function to handle changes to list of
	 * available organization people.
	 */
	onPersonChange: PropTypes.func.isRequired,

	/**
	 * Callback function to handle field changes.
	 */
	onChange: PropTypes.func.isRequired,

};

const CertificateTemplateEditor = (props) => {

	const { index, widescreen, formData, layout, people, organization, filestack,
		onPersonChange, onChange } = props;

	// Get template form certificate form data
	const template = formData.certificate_templates[index];

	// Add person data to template signatures
	const templateSignatures = template.certificate_people &&
		sortByWeight(template.certificate_people.map((certificatePerson) => {

			const result = Object.assign({}, certificatePerson);

			const associatedPerson = people.find((person) => person.id === certificatePerson.person_id);

			if (associatedPerson) {
				result.name = associatedPerson.name;
				result.affiliation = associatedPerson.affiliation;
				result.signature = associatedPerson.signature;
			}

			return result;

		}));

	const handleOnInputChange = (fieldName, fieldValue) => {

		const updatedTemplates = [...formData.certificate_templates];

		updatedTemplates[index] = Object.assign({}, template, {
			[fieldName]: fieldValue,
		});

		onChange('certificate_templates', updatedTemplates);

	};

	const preview = (
		<div
			className={`${widescreen ? 'w-50' : 'w-100'} ${styles['certificate-preview-container']}`}>
			<CertificatePreview
				layout={layout}
				template={template}
				signatures={templateSignatures}
				formData={formData}
				organization={organization} />
		</div>
	);

	return (
		<div
			className={`${widescreen ? '' : 'flex-wrap'} ${styles['certificate-template-editor']}`}>
			{!widescreen && preview}
			<div
				className={`${widescreen ? 'w-50' : 'w-100'} ${styles['template-input']}`}>
				<FormGroup className="mb-5">
					<Label
						className="mb-1"
						for="body">
						<FormattedMessage {...messages.TC_COMPONENTS_CERTIFICATE_EDITOR_FIELD_LABEL_BODY} />
					</Label>
					<TextEditor
						innerClassName="certificate-body-editor p-2"
						editorDomId="certificate-body"
						config={{
							heading: true,
							bold: true,
							italics: true,
							underline: true,
							history: true,
							list: true,
							link: true,
							image: true,
						}}
						content={template.content || emptyState}
						filestackSettings={filestack}
						onChange={(value) => handleOnInputChange('content', value)} />
				</FormGroup>
				<FormGroup className="mb-5">
					<Label
						className="mb-1"
						for="certificate_people">
						<FormattedMessage {...messages.TC_COMPONENTS_CERTIFICATE_EDITOR_FIELD_LABEL_SIGNATURES} />
					</Label>
					<TemplateSignatures
						signatures={templateSignatures}
						people={people}
						templateID={template.id}
						filestack={filestack}
						onPersonChange={onPersonChange}
						onChange={handleOnInputChange} />
				</FormGroup>
			</div>
			{widescreen && preview}
		</div>
	);

};

CertificateTemplateEditor.propTypes = propTypes;

export default CertificateTemplateEditor;
